import { Badge } from "@repo/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/dialog";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { traceValuationResult, type ValuationTraceResult } from "@/lib/api";

interface TraceResultDialogProps {
  runId: string | null;
  saleMonth: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatMonth(value: string): string {
  return new Intl.DateTimeFormat("en-AU", { month: "short", year: "numeric" }).format(
    new Date(value),
  );
}

function formatCurrency(value: number | string | null | undefined): string {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(numeric);
}

function formatDate(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-AU", { day: "2-digit", month: "short", year: "2-digit" }).format(
    date,
  );
}

export function TraceResultDialog({ runId, saleMonth, open, onOpenChange }: TraceResultDialogProps) {
  const [trace, setTrace] = useState<ValuationTraceResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !runId || !saleMonth) {
      setTrace(null);
      setError(null);
      return;
    }
    let cancelled = false;
    async function load(id: string, month: string) {
      try {
        setIsLoading(true);
        setError(null);
        const next = await traceValuationResult({ runId: id, saleMonth: month, sampleLimit: 25 });
        if (!cancelled) setTrace(next);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to trace result");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load(runId, saleMonth);
    return () => {
      cancelled = true;
    };
  }, [open, runId, saleMonth]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Trace · {saleMonth ? formatMonth(saleMonth) : "—"}
          </DialogTitle>
          <DialogDescription>
            Live recomputation of this month's value from the run's source-selection manifest.
            Sample rows show up to 25 dedup'd sales sorted by date.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Recomputing trace...
          </p>
        ) : null}

        {error ? (
          <p className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            {error}
          </p>
        ) : null}

        {trace ? (
          <div className="space-y-4">
            <div className="rounded-md border p-3">
              <p className="text-xs font-medium uppercase text-muted-foreground">Formula</p>
              <p className="mt-1 font-mono text-sm">{trace.trace?.formula ?? "n/a"}</p>
              {trace.trace?.formulaInputs ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  n = {trace.trace.formulaInputs.n} · sum = {formatCurrency(trace.trace.formulaInputs.sum)}
                </p>
              ) : null}
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground">Average</p>
                  <p className="font-semibold">{formatCurrency(trace.output?.average_price)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Median</p>
                  <p className="font-semibold">{formatCurrency(trace.output?.median_price)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Sample size</p>
                  <p className="font-semibold">
                    {trace.counts.monthTotal.toLocaleString()} sales
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Outliers removed</p>
                  <p className="font-semibold">{trace.trace?.outliersRemoved ?? 0}</p>
                </div>
              </div>
            </div>

            <div className="rounded-md border p-3">
              <p className="text-xs font-medium uppercase text-muted-foreground">
                Source contribution
              </p>
              <div className="mt-2 flex flex-wrap gap-2 text-sm">
                <Badge variant="default">
                  Internal: {trace.contribution.internal_property_snapshots.toLocaleString()}
                </Badge>
                <Badge variant="secondary">
                  SQM: {trace.contribution.sqm_back_history.toLocaleString()}
                </Badge>
                <Badge variant="outline">
                  Duplicates dropped: {trace.counts.duplicatesDroppedAcrossRun.toLocaleString()}
                </Badge>
              </div>
            </div>

            <div className="rounded-md border">
              <div className="border-b p-3">
                <p className="text-xs font-medium uppercase text-muted-foreground">
                  Sample rows ({trace.counts.monthSampleReturned} of {trace.counts.monthTotal})
                </p>
              </div>
              <ul className="divide-y text-sm">
                {trace.sampleRows.length === 0 ? (
                  <li className="p-3 text-muted-foreground">No sales in this month.</li>
                ) : null}
                {trace.sampleRows.map((row, index) => (
                  <li
                    key={`${formatDate(row.saleDate)}-${row.salePrice}-${index}`}
                    className="flex items-center justify-between gap-2 p-3"
                  >
                    <div>
                      <p className="font-medium">{formatCurrency(row.salePrice)}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(row.saleDate)} ·{" "}
                        {row.propertyType ?? "type unknown"}
                      </p>
                    </div>
                    {row.saleEventKey ? (
                      <Badge variant="outline" className="font-mono text-[10px]">
                        evt
                      </Badge>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
